import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Navbar, Footer } from '@/components/Layout';
import { Play, Download, FileText, ExternalLink } from 'lucide-react';

/*
 * /manifesto: the permanent, public, indexable landing page for the
 * Recourse Manifesto. Holds only visitor-facing content:
 *   - The tagline hero
 *   - The "Why Recourse Matters" narrative
 *   - The 45-second film card
 *   - The verifier link block
 *   - Closing CTA
 */

// ── Verifier link ─────────────────────────────────────────────────────────
function VerifierLink({ label, sublabel, href, external }) {
  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className="flex items-center justify-between gap-3 px-4 py-3.5 rounded-lg border border-white/10 bg-white/[0.02] hover:bg-emerald-500/[0.06] hover:border-emerald-500/40 transition-colors group"
      data-testid={`verifier-link-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
    >
      <div>
        <div className="text-sm font-bold text-white group-hover:text-emerald-300 transition-colors">{label}</div>
        <div className="text-[11px] text-slate-500 mt-0.5">{sublabel}</div>
      </div>
      <ExternalLink className="w-4 h-4 text-slate-500 group-hover:text-emerald-400 transition-colors flex-shrink-0" />
    </a>
  );
}

export default function Manifesto() {
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="manifesto-page">
      <Helmet>
        <title>The Recourse Manifesto · Kojiru</title>
        <meta name="description" content="When an agent harms a consumer, the merchant has a signed mandate, a public operator pledge, and on-chain evidence to resolve it through their existing dispute policy. Kojiru is infrastructure, not insurance. A 45-second film + the full narrative." />
        <meta property="og:title" content="The Recourse Manifesto · Kojiru" />
        <meta property="og:description" content="Give the consumers what they want. 45-second film + the full narrative. Recourse for agentic commerce, on Base." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://kojiru.com/manifesto" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="The Recourse Manifesto · Kojiru" />
        <link rel="alternate" type="application/ld+json" href="/kojiru-schema.jsonld" />
        <link rel="alternate" type="application/json" href="/.well-known/kypa" title="KYPA Protocol Manifest" />
      </Helmet>

      <Navbar />

      {/* ─── HERO ─────────────────────────────────────────────────── */}
      <section className="relative pt-32 pb-16 overflow-hidden" data-testid="manifesto-hero">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/6 w-[500px] h-[500px] bg-emerald-600/[0.06] rounded-full blur-[150px]" />
        </div>
        <div className="container mx-auto px-6 relative z-10 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[11px] tracking-[3px] text-emerald-300 font-bold uppercase">Position · 45-second Film · Voiceover</span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight mb-8" data-testid="manifesto-hero-heading">
              Give the consumers<br />
              <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                what they want.
              </span>
            </h1>

            <p className="text-xl sm:text-2xl text-slate-300 leading-relaxed max-w-3xl mb-10">
              When an agent harms a consumer, the merchant holds a signed mandate, a public operator pledge, and on-chain evidence to resolve it through their existing dispute policy. Kojiru is infrastructure, not insurance.
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/film/recourse"
                data-testid="manifesto-watch-film"
                className="inline-flex items-center gap-3 px-7 py-4 rounded-full bg-white text-black font-bold hover:bg-slate-200 transition-colors"
              >
                <Play className="w-5 h-5 fill-current" />
                Watch the film · 45 sec
              </Link>
              <a
                href="/downloads/kojiru-recourse-voiceover.mp3"
                download
                data-testid="manifesto-download-mp3"
                className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-white/20 text-white font-bold hover:bg-white/5 transition-colors"
              >
                <Download className="w-4 h-4" />
                Voiceover MP3
              </a>
              <a
                href="/downloads/kojiru-agentic-commerce-2030-two-billion-agents.pdf"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="manifesto-download-paper"
                className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-emerald-500/40 bg-emerald-500/5 text-emerald-300 font-bold hover:bg-emerald-500/10 transition-colors"
              >
                <FileText className="w-4 h-4" />
                Full position paper
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── WHY RECOURSE MATTERS ─────────────────────────────────── */}
      <section className="py-20 border-t border-white/5" data-testid="manifesto-narrative">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="text-[11px] tracking-[3px] text-emerald-400 font-bold uppercase mb-4">
            Why Recourse Should Matter to You
          </div>
          <h2 className="text-3xl sm:text-4xl font-black mb-8 leading-tight">
            In an imperfect world,<br />
            <span className="text-emerald-400">recourse is essential.</span>
          </h2>
          <div className="prose prose-invert prose-lg max-w-none text-slate-300 leading-relaxed space-y-6">
            <p>
              Neither people nor autonomous AI agents can make every agentic commerce financial
              transaction flawless. Commerce will always carry the risk of error, misuse, or harm.
            </p>
            <p>
              The card networks were designed around a two-category world: a transaction is either
              <strong className="text-white"> authorized</strong> or <strong className="text-white">unauthorized</strong>.
              Fraud is the unauthorized case. Chargebacks are the enforcement mechanism.
            </p>
            <p>
              Agentic commerce introduces something messier: <strong className="text-emerald-300">authorized delegation,
              but incorrect execution</strong>. A consumer authorizes an agent to book a hotel for $200; the agent books a
              $600 room. Under existing card rules, the consumer authorized the agent, so the consumer authorized the
              charge. The merchant is paid. The bank cannot chargeback. The consumer eats the loss.
            </p>
            <p>
              This is not a fraud problem. Fraud is solved. It is not an identity problem. Identity is solved. It is a
              <strong className="text-white"> recourse problem</strong> and no existing layer of the payment stack owns
              it.
            </p>
            <p className="text-white font-semibold text-xl border-l-4 border-emerald-500 pl-5">
              Kojiru is the evidence layer for that gap: a signed mandate, and a public USDC pledge the agent
              posted <em>before</em> it ever went to work.
            </p>
          </div>
        </div>
      </section>

      {/* ─── THE TWO MOMENTS ────────────────────────────────────── */}
      <section className="py-20 border-t border-white/5" data-testid="manifesto-two-moments">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-[11px] tracking-[3px] text-emerald-400 font-bold uppercase mb-3">
            The Two Moments the Journey Has Left
          </div>
          <h2 className="text-3xl sm:text-4xl font-black leading-tight mb-6">
            When agents run the middle, humanity concentrates at <span className="text-emerald-400">two touchpoints</span>.
          </h2>
          <p className="text-slate-400 leading-relaxed mb-10 max-w-3xl">
            The Mandate (a human briefs the agent) and the Outcome (a human receives what was bought). Kojiru turns the first into a signed credential and the second into a public, on-chain evidence record when things go wrong.
          </p>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-6" data-testid="two-moments-mandate">
              <div className="text-[10px] tracking-[3px] text-emerald-400 font-bold uppercase mb-2">The Mandate Moment</div>
              <h3 className="text-xl font-black mb-3 text-white">A signed credential, not slide copy.</h3>
              <ul className="text-sm text-slate-300 space-y-2 leading-relaxed">
                <li><strong className="text-white">What can the agent buy?</strong> Category + recipient allowlist</li>
                <li><strong className="text-white">What is the ceiling?</strong> <code className="text-emerald-300 text-xs">max_amount_cents</code></li>
                <li><strong className="text-white">Who holds the pledge?</strong> KYPA tier (Bronze &rarr; Platinum)</li>
                <li><strong className="text-white">What if it goes wrong?</strong> Public evidence, next to a public pledge.</li>
              </ul>
            </div>
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.04] p-6" data-testid="two-moments-outcome">
              <div className="text-[10px] tracking-[3px] text-emerald-400 font-bold uppercase mb-2">The Outcome Moment</div>
              <h3 className="text-xl font-black mb-3 text-white">Your only emotional touchpoint left.</h3>
              <ul className="text-sm text-slate-300 space-y-2 leading-relaxed">
                <li><strong className="text-white">Good outcome</strong> &rarr; onboarding transfers, ACS ticks up, mandate renews. Nothing to design.</li>
                <li><strong className="text-white">Bad outcome</strong> &rarr; on-chain dispute, oracle verifies, the breach is recorded next to the operator&rsquo;s pledge. Remedies stay with the merchant&rsquo;s dispute policy.</li>
                <li><strong className="text-white">No support ticket.</strong> No 40-day fight. Next mandate written with the same confidence as the first.</li>
              </ul>
            </div>
          </div>
          <div className="mt-8 text-center flex flex-wrap items-center justify-center gap-3">
            <a
              href="/downloads/kojiru-two-moments-mandate-outcome.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-emerald-500/40 bg-emerald-500/5 text-emerald-300 font-bold hover:bg-emerald-500/10 transition-colors"
              data-testid="two-moments-download"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" /></svg>
              Read the full paper (PDF)
            </a>
            <Link
              to="/how-a-dispute-works"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 bg-white text-black font-bold hover:bg-slate-100 transition-colors"
              data-testid="two-moments-how-it-works"
            >
              See how a dispute actually works &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* ─── FILM CARD ─────────────────────────────────────────────── */}
      <section className="py-16 border-t border-white/5" data-testid="manifesto-film-card">
        <div className="container mx-auto px-6 max-w-5xl">
          <Link
            to="/film/recourse"
            className="block group"
            data-testid="manifesto-film-card-link"
          >
            <div className="relative aspect-video rounded-2xl overflow-hidden border border-white/10 bg-black hover:border-emerald-500/40 transition-colors">
              <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f] to-black" />
              <div className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center">
                <div className="text-[10px] tracking-[4px] text-emerald-400 mb-4">
                  KOJIRU · 45-SECOND FILM · ONYX NARRATION
                </div>
                <h3 className="text-4xl sm:text-6xl font-black text-white mb-6 tracking-tight leading-[1.05]">
                  The Recourse Manifesto
                </h3>
                <div className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-black font-bold group-hover:bg-emerald-400 group-hover:scale-105 transition-all">
                  <Play className="w-5 h-5 fill-current" />
                  Play the film
                </div>
                <div className="mt-8 text-sm tracking-[3px] text-slate-500">
                  GIVE THE CONSUMERS · WHAT THEY WANT
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* ─── VERIFIER BLOCK ──────────────────────────────────────── */}
      <section className="py-20 border-t border-white/5" data-testid="manifesto-verifier">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="mb-10">
            <div className="text-[11px] tracking-[3px] text-emerald-400 font-bold uppercase mb-3">
              Verify every claim
            </div>
            <h2 className="text-3xl sm:text-4xl font-black leading-tight mb-3">
              One click each. On-chain and live.
            </h2>
            <p className="text-slate-400 max-w-2xl">
              Nothing in the film or the paper is speculative. Every claim resolves to a
              live contract on Base L2, a live endpoint on kojiru.com, or a signed record in a public repository.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <VerifierLink
              label="Mainnet Hook · USDC-bound"
              sublabel="BaseScan source-verified · 0x05F2…0874"
              href="https://basescan.org/address/0x05F2541827104393257FEc27C61b8Ea5373e0874"
              external
            />
            <VerifierLink
              label="KYPA Directory"
              sublabel="Every pledged agent · public registry"
              href="/kypa/directory"
            />
            <VerifierLink
              label="First on-chain slash proof"
              sublabel="Sepolia · signed JSON packet"
              href="/proofs/slash-sepolia-000001.json"
              external
            />
            <VerifierLink
              label="Prevention and Evidence Simulator"
              sublabel="Model your own ROI · dynamic OG card"
              href="/for-merchants#simulator"
            />
            <VerifierLink
              label="For Issuing Programs"
              sublabel="Two-second authorization callback"
              href="/for-issuers"
            />
            <VerifierLink
              label="For Underwriters"
              sublabel="From uninsurable to under $5K residual"
              href="/for-underwriters"
            />
            <VerifierLink
              label="Full Position Paper (PDF)"
              sublabel="Agentic Commerce for 2030 & Beyond"
              href="/downloads/kojiru-agentic-commerce-2030-two-billion-agents.pdf"
              external
            />
            <VerifierLink
              label="Recourse Blueprint (GitHub)"
              sublabel="Reference SDK · 18 pytest tests passing"
              href="https://github.com/kojiru/kojiru-recourse-blueprint"
              external
            />
          </div>
        </div>
      </section>

      {/* ─── CLOSING CTA ─────────────────────────────────────────── */}
      <section className="py-24 border-t border-white/5" data-testid="manifesto-closing">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <h2 className="text-3xl sm:text-5xl font-black leading-[1.1] mb-8">
            When an AI agent makes a promise,<br />
            <span className="text-emerald-400">someone should be able to make it keep it.</span>
          </h2>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/film/recourse"
              data-testid="manifesto-closing-watch"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-black font-bold hover:bg-slate-200 transition-colors"
            >
              <Play className="w-5 h-5 fill-current" />
              Watch the film
            </Link>
            <Link
              to="/register-agent?mode=kypa"
              data-testid="manifesto-closing-enroll"
              className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-emerald-500/40 bg-emerald-500/5 text-emerald-300 font-bold hover:bg-emerald-500/10 transition-colors"
            >
              Enroll your agent
            </Link>
          </div>
          <p className="mt-8 text-xs text-slate-500 tracking-wider">
            KOJIRU · THE RECOURSE LAYER FOR AGENTIC COMMERCE · NASHVILLE, TN
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
